import { useContext, useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Select from "react-select";
import {
  SKOR_TICKET_CATEGORIES,
  DOCUMENT_REQUIRED_CATEGORIES,
  getTicketCategoriesForUserType,
} from "./skorTicketCategories";
import {
  FiPaperclip,
  FiUser,
  FiX,
  FiPlus,
  FiHome,
  FiFileText,
} from "react-icons/fi";
import useTranslator from "../../hooks/useTranslator";
import axiosInstance from "../../services/authWrapper";
import {
  createSkorTicket,
  searchTicketLinks,
} from "../../services/skorTicketService";
import {
  getUserDisplayName,
  toast,
  formatPropertyAddress,
} from "../../helper/common";
import constants from "../../Utils/constants";
import TenantDocumentListsContext from "../../context/tenantDocumentLists/TenantDocumentListsContext";
import styles from "./SkorTicket.module.css";

const ACCEPTED_FILE_TYPES = ["image/png", "image/jpeg", "application/pdf"];
const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Two chip rows: Applications (applications and tenant profiles merged —
// tenants are treated as applications) and Properties. Each row lists its
// selected chips plus a plus button that opens a type-scoped search.
const LINK_GROUPS = [
  {
    linkedType: "Application",
    searchTypes: ["Application", "Tenant"],
    Icon: FiFileText,
    labelKey: "skorTicketApplications",
    labelFallback: "Applications",
    addKey: "skorTicketAddApplication",
    addFallback: "Add application",
    searchKey: "skorTicketSearchApplications",
    searchFallback: "Search applications…",
  },
  {
    linkedType: "Property",
    searchTypes: ["Property"],
    Icon: FiHome,
    labelKey: "skorTicketProperties",
    labelFallback: "Properties",
    addKey: "skorTicketAddProperty",
    addFallback: "Add property",
    searchKey: "skorTicketSearchProperties",
    searchFallback: "Search properties…",
  },
];

// The landlord missing list (profession_docs, from the backend). The modal
// does not wait for the lists, so a ticket can always be sent: until they are
// loaded (or if they cannot be loaded) no document is marked compulsory.
const getCompulsoryDocNames = (tenant, professionDocs) => {
  const docsByStatus = professionDocs ?? {};
  if (tenant?.employment_status === "student") {
    return Object.keys(docsByStatus.STUDENT_DOCS ?? {});
  }
  if (tenant?.employment_status === "employed") {
    return Object.keys(
      tenant?.employment_category === constants.domestic_worker
        ? (docsByStatus.DOMESTIC_WORKER_DOCS ?? {})
        : (docsByStatus.EMPLOYED_DOCS ?? {})
    );
  }
  if (
    tenant?.employment_status === "self_employed" &&
    tenant?.employment_category === "freelancer"
  ) {
    return Object.keys(docsByStatus.FREELANCER_DOCS ?? {});
  }
  if (
    tenant?.employment_status === "self_employed" &&
    tenant?.employment_category === "ownACompany"
  ) {
    return Object.keys(docsByStatus.SELF_EMPLOYED_DOCS ?? {});
  }
  if (tenant?.employment_status === "retired") {
    return Object.keys(docsByStatus.RETIRED_DOCS ?? {});
  }
  if (tenant?.employment_status === "unemployed") {
    return Object.keys(docsByStatus.UNEMPLOYED_DOCS ?? {});
  }
  if (tenant?.employment_status === "public_servant") {
    return Object.keys(docsByStatus.PUBLIC_SERVANT_DOCS ?? {});
  }
  if (tenant?.employment_status === "company") {
    return Object.keys(docsByStatus.COMPANY_DOCS ?? {});
  }
  return [];
};

export default function CreateSkorTicketModal({
  tenant,
  isGuarantor,
  onClose,
  disablePortal = false,
  container,
}) {
  const { _t } = useTranslator();
  // null until the document lists are loaded (see getCompulsoryDocNames)
  const professionDocs =
    useContext(TenantDocumentListsContext)?.lists?.profession_docs ?? null;
  // Uniform context links (Tenant, Application, Property). tenant_id is
  // derived as the first Tenant link for backwards-compatible reads.
  const initialLinks = useMemo(() => {
    if (!tenant?._id) return [];
    return [
      {
        linked_type: "Tenant",
        linked_id: String(tenant._id),
        label: getUserDisplayName(tenant, "") || tenant?.email || "Profile",
        subtitle: tenant?.email || "Profile",
      },
    ];
  }, []);
  const [links, setLinks] = useState(initialLinks);
  const primaryTenantId =
    links.find((link) => link.linked_type === "Tenant")?.linked_id || null;
  // Tenants never search: their ticket is always linked to their own
  // profile. Only landlords get the per-type search rows.
  const isTenantUser = localStorage.getItem("usertype") === "tenant";
  // Per-type search: one picker open at a time (Tenant, Application, Property).
  const [activePicker, setActivePicker] = useState(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerOptions, setPickerOptions] = useState([]);
  const [searchingPicker, setSearchingPicker] = useState(false);
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("");
  const [urgency, setUrgency] = useState("medium");
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [documents, setDocuments] = useState(tenant?.tenantDocs || []);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentLoadFailed, setDocumentLoadFailed] = useState(false);
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState("");
  const [fileError, setFileError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const submitting = useRef(false);
  const fileInput = useRef(null);
  const canUnlinkProfile = localStorage.getItem("usertype") === "landlord";
  // Categories depend on who is reporting, not on whose profile is linked —
  // a landlord viewing a tenant profile still sees the landlord list.
  const visibleCategories = useMemo(
    () => getTicketCategoriesForUserType(localStorage.getItem("usertype")),
    []
  );
  const categorySettings = SKOR_TICKET_CATEGORIES.find(
    (entry) => entry.value === category
  ) || {
    translationKey: "skorTicket",
    subjectPlaceholder: "Briefly describe your issue",
    messagePlaceholder:
      "Describe your question or issue and include any useful details.",
  };
  const requiresDocuments = DOCUMENT_REQUIRED_CATEGORIES.includes(category);
  const canSubmit =
    Boolean(category && subject.trim() && message.trim()) &&
    (!requiresDocuments ||
      Boolean(primaryTenantId && selectedDocuments.length)) &&
    !isSubmitting;

  useEffect(() => {
    if (tenant?.tenantDocs) setDocuments(tenant.tenantDocs);
  }, [tenant?._id]);

  // Debounced per-group search. The Applications row searches tenants and
  // applications together and merges both into one option list. Only
  // records the caller may link are returned (same rules as creation).
  const activeGroup = LINK_GROUPS.find(
    (group) => group.linkedType === activePicker
  );
  useEffect(() => {
    if (!activePicker || !activeGroup) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSearchingPicker(true);
      try {
        const results = await searchTicketLinks({
          search: pickerSearch.trim(),
          types: activeGroup.searchTypes.join(","),
        });
        if (controller.signal.aborted) return;
        const selectedKeys = new Set(
          links.map((link) => `${link.linked_type}:${link.linked_id}`)
        );
        setPickerOptions(
          (Array.isArray(results) ? results : [])
            .filter(
              (entry) =>
                entry?.linked_id &&
                activeGroup.searchTypes.includes(entry.linked_type) &&
                !selectedKeys.has(`${entry.linked_type}:${entry.linked_id}`)
            )
            .map((entry) => ({
              value: `${entry.linked_type}:${entry.linked_id}`,
              label: entry.label,
              linked_type: entry.linked_type,
              linked_id: String(entry.linked_id),
              displayLabel: entry.label,
              subtitle: entry.subtitle,
              detail: entry.detail || null,
            }))
        );
      } catch {
        if (!controller.signal.aborted) setPickerOptions([]);
      } finally {
        if (!controller.signal.aborted) setSearchingPicker(false);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [activePicker, pickerSearch, links]);

  // Display name for a picked record, using the same helpers as the rest
  // of the app: tenant display name (onfido → forensic → first/last) and
  // the formatted property address.
  const displayNameForLink = (option) => {
    if (!option) return "";
    if (option.linked_type === "Tenant" && option.detail) {
      return (
        getUserDisplayName(
          {
            fname: option.detail.fname,
            lname: option.detail.lname,
            forensic_name: option.detail.forensic_name,
          },
          ""
        ) ||
        option.displayLabel ||
        option.label
      );
    }
    if (option.linked_type === "Property" && option.detail) {
      return (
        formatPropertyAddress(
          option.detail,
          localStorage.getItem("i18nextLng") || "es"
        ) ||
        option.displayLabel ||
        option.label
      );
    }
    return option.displayLabel || option.label;
  };

  // Subtitle shown below the name in picker rows: email for tenants,
  // reference for properties, nothing for applications.
  const subtitleForLinkOption = (option) => {
    if (!option) return "";
    if (option.linked_type === "Tenant")
      return option.detail?.email || option.subtitle || "";
    if (option.linked_type === "Property")
      return option.detail?.property_reference || "";
    return "";
  };

  const formatLinkOptionLabel = (option) => {
    const subtitle = subtitleForLinkOption(option);
    return (
      <span className={styles.optionLabel}>
        <span className={styles.optionMain}>{displayNameForLink(option)}</span>
        {subtitle ? <span className={styles.optionSub}>{subtitle}</span> : null}
      </span>
    );
  };

  const addLink = (option) => {
    if (!option || links.length >= 10) return;
    const key = `${option.linked_type}:${option.linked_id}`;
    if (links.some((link) => `${link.linked_type}:${link.linked_id}` === key)) {
      return;
    }
    const label = displayNameForLink(option);
    setLinks((current) => [
      ...current,
      {
        linked_type: option.linked_type,
        linked_id: String(option.linked_id),
        label,
        subtitle: subtitleForLinkOption(option) || option.subtitle,
      },
    ]);
    // Keep the picker open for multi-add; collapse only at the link cap.
    if (links.length + 1 >= 10) {
      setActivePicker(null);
    }
    setPickerSearch("");
  };

  const removeLink = (link) => {
    setLinks((current) =>
      current.filter(
        (entry) =>
          `${entry.linked_type}:${entry.linked_id}` !==
          `${link.linked_type}:${link.linked_id}`
      )
    );
    if (link.linked_type === "Tenant") setSelectedDocuments([]);
  };

  const togglePicker = (linkedType) => {
    setPickerSearch("");
    setPickerOptions([]);
    setActivePicker((current) => (current === linkedType ? null : linkedType));
  };

  useEffect(() => {
    if (
      !primaryTenantId ||
      (tenant?._id === primaryTenantId && tenant?.tenantDocs)
    ) {
      setLoadingDocuments(false);
      return;
    }
    const controller = new AbortController();
    setLoadingDocuments(true);
    setDocumentLoadFailed(false);
    axiosInstance
      .get(`${process.env.REACT_APP_SERVER_URL}/tenants/getUploadedDocuments`, {
        params: {
          tenantId: primaryTenantId,
          ...(isGuarantor ? { guarantor: true } : {}),
        },
        signal: controller.signal,
      })
      .then(({ data }) => {
        setDocuments(data?.success_res?.tenantDocs || []);
      })
      .catch((failure) => {
        if (failure.code !== "ERR_CANCELED") setDocumentLoadFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingDocuments(false);
      });
    return () => controller.abort();
  }, [primaryTenantId, isGuarantor]);

  const compulsoryDocNames = useMemo(
    () =>
      primaryTenantId ? getCompulsoryDocNames(tenant, professionDocs) : [],
    [
      primaryTenantId,
      tenant?.employment_status,
      tenant?.employment_category,
      professionDocs,
    ]
  );

  const documentNames = useMemo(() => {
    const uploadedNames = documents
      .map((document) => document?.type || document?.file_name)
      .filter(
        (name) =>
          name &&
          !["uploaded-from-onboarding", "uploaded-from-onboardingg"].includes(
            name.trim().toLowerCase()
          )
      );
    // "Missing documents" and "Classification of documents" tickets must
    // point at a required document the tenant has not uploaded yet.
    if (
      category === "missing_documents" ||
      category === "document_classification"
    ) {
      const uploadedSet = new Set(
        uploadedNames.map((name) => name.trim().toLowerCase())
      );
      const missingNames = compulsoryDocNames.filter(
        (name) => !uploadedSet.has(name.trim().toLowerCase())
      );
      // Nothing missing (all compulsory docs uploaded): fall back to the
      // employment-type list so the required field never blocks submission.
      return missingNames.length > 0 ? missingNames : compulsoryDocNames;
    }
    return [...new Set([...compulsoryDocNames, ...uploadedNames])];
  }, [compulsoryDocNames, documents, category]);
  const documentOptions = documentNames.map((name) => ({
    value: name,
    label: _t(name, "", "fixed"),
  }));
  const availableDocumentValues = useMemo(
    () => new Set(documentOptions.map((option) => option.value)),
    [documentOptions]
  );

  // Drop selected documents that are no longer offered (e.g. after switching
  // category between the missing-documents list and the full list).
  useEffect(() => {
    setSelectedDocuments((current) => {
      if (!current.length) return current;
      const pruned = current.filter((document) =>
        availableDocumentValues.has(document.value)
      );
      return pruned.length === current.length ? current : pruned;
    });
  }, [availableDocumentValues]);

  const addAttachments = (files) => {
    if (submitting.current || isSubmitting) return;
    const selectedFiles = Array.from(files || []);
    if (
      selectedFiles.some(
        (file) =>
          !ACCEPTED_FILE_TYPES.includes(file.type) ||
          !file.size ||
          file.size > MAX_FILE_SIZE
      ) ||
      attachments.length >= MAX_ATTACHMENTS ||
      attachments.length + selectedFiles.length > MAX_ATTACHMENTS
    ) {
      setFileError(true);
      return;
    }
    setFileError(false);
    setAttachments((current) => {
      const result = [...current];
      selectedFiles.forEach((file) => {
        if (result.length >= MAX_ATTACHMENTS) return;
        if (
          !result.some(
            (existing) =>
              existing.name === file.name &&
              existing.size === file.size &&
              existing.lastModified === file.lastModified
          )
        )
          result.push(file);
      });
      return result;
    });
  };

  const submitTicket = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    if (!category) {
      setError("category");
      return;
    }
    if (!subject.trim() || !message.trim()) {
      setError("required");
      return;
    }
    if (
      requiresDocuments &&
      (!primaryTenantId || selectedDocuments.length === 0)
    ) {
      setError("documents");
      return;
    }
    submitting.current = true;
    setIsSubmitting(true);
    setError("");
    try {
      await createSkorTicket({
        tenant_id: primaryTenantId,
        links: links.map((link) => ({
          linked_type: link.linked_type,
          linked_id: link.linked_id,
        })),
        subject: subject.trim(),
        category,
        urgency,
        document_names: primaryTenantId
          ? selectedDocuments.map((document) => document.value)
          : [],
        message: message.trim(),
        attachments,
      });
      toast.success(_t("skorTicketCreated", "Your ticket has been submitted."));
      onClose();
    } catch {
      setError("submit");
    } finally {
      submitting.current = false;
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    if (!submitting.current) onClose();
  };

  return (
    <Dialog
      disablePortal={disablePortal}
      container={container}
      onKeyDown={(event) => event.stopPropagation()}
      open
      onClose={closeModal}
      maxWidth={false}
      aria-labelledby="skor-ticket-title"
      aria-describedby="skor-ticket-description"
      PaperProps={{ className: styles.modal }}
    >
      <form onSubmit={submitTicket} className={styles.form}>
        <div className={styles.heading}>
          <h2 id="skor-ticket-title">
            {_t("skorTicketCreate", "Create ticket")}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            disabled={isSubmitting}
            onClick={closeModal}
            aria-label={_t("skorTicketClose", "Close")}
          >
            <FiX aria-hidden="true" />
          </button>
        </div>
        <p id="skor-ticket-description" className={styles.description}>
          {_t(
            "skorTicketDescription",
            "Describe the issue. Our support team will review your ticket."
          )}
        </p>
        <fieldset disabled={isSubmitting} className={styles.fields}>
          <div className={styles.field}>
            <div className={styles.linkedRow}>
              <span id="skor-ticket-links-label">
                {_t("skorTicketLinkedTo", "Linked to")}
              </span>
              {links.length === 0 && (
                <span className={styles.profileChip}>
                  {canUnlinkProfile
                    ? _t(
                        "skorTicketGeneralWorkspace",
                        "General workspace ticket"
                      )
                    : _t("skorTicketGeneral", "General ticket")}
                </span>
              )}
              {isTenantUser &&
                links
                  .filter((link) => link.linked_type === "Tenant")
                  .map((link) => (
                    <span
                      key={`${link.linked_type}:${link.linked_id}`}
                      className={styles.profileChip}
                    >
                      <FiUser aria-hidden="true" />
                      <span>
                        {_t("skorTicketProfile", "Profile")} · {link.label}
                      </span>
                    </span>
                  ))}
            </div>
            {!isTenantUser && (
              <div className={styles.linkGroupsRow}>
                {LINK_GROUPS.map((group) => {
                  const GroupIcon = group.Icon;
                  const groupLinks = links.filter((link) =>
                    group.searchTypes.includes(link.linked_type)
                  );
                  const pickerOpen = activePicker === group.linkedType;
                  return (
                    <div key={group.linkedType} className={styles.linkGroup}>
                      <div className={styles.linkedRow}>
                        <span className={styles.linkGroupLabel}>
                          <GroupIcon aria-hidden="true" />
                          {_t(group.labelKey, group.labelFallback)}
                        </span>
                        {groupLinks.map((link) => (
                          <span
                            key={`${link.linked_type}:${link.linked_id}`}
                            className={styles.profileChip}
                          >
                            <span>{link.label}</span>
                            <button
                              type="button"
                              className={styles.removeButton}
                              aria-label={_t(
                                "skorTicketRemoveLink",
                                "Remove {{label}}",
                                "translation",
                                { label: link.label }
                              )}
                              onClick={() => removeLink(link)}
                            >
                              <FiX aria-hidden="true" />
                            </button>
                          </span>
                        ))}
                        {links.length < 10 && (
                          <button
                            type="button"
                            className={styles.addLinkButton}
                            aria-expanded={pickerOpen}
                            aria-label={_t(group.addKey, group.addFallback)}
                            title={_t(group.addKey, group.addFallback)}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => togglePicker(group.linkedType)}
                          >
                            <FiPlus aria-hidden="true" />
                          </button>
                        )}
                      </div>
                      {pickerOpen && (
                        <div className={styles.pickerRow}>
                          <Select
                            className={styles.documentSelect}
                            menuPlacement="bottom"
                            inputId={`skor-ticket-links-${group.linkedType}`}
                            autoFocus
                            isClearable
                            isLoading={searchingPicker}
                            isDisabled={isSubmitting || links.length >= 10}
                            options={pickerOptions}
                            value={null}
                            closeMenuOnSelect={false}
                            inputValue={pickerSearch}
                            onInputChange={(value, { action }) => {
                              if (action === "input-change")
                                setPickerSearch(value);
                            }}
                            onChange={addLink}
                            classNamePrefix="skor-ticket-select"
                            formatOptionLabel={formatLinkOptionLabel}
                            loadingMessage={() =>
                              _t("skorTicketSearching", "Searching…")
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Escape") setActivePicker(null);
                            }}
                            placeholder={_t(
                              group.searchKey,
                              group.searchFallback
                            )}
                            noOptionsMessage={() =>
                              searchingPicker
                                ? _t("skorTicketSearching", "Searching…")
                                : pickerSearch.trim()
                                  ? _t(
                                      "skorTicketNoLinks",
                                      "No matching records"
                                    )
                                  : _t(
                                      "skorTicketTypeToSearch",
                                      "Type to search"
                                    )
                            }
                            filterOption={null}
                          />
                          <button
                            type="button"
                            className={styles.closeButton}
                            aria-label={_t(
                              "skorTicketCloseSearch",
                              "Close search"
                            )}
                            onClick={() => {
                              setActivePicker(null);
                              setPickerSearch("");
                            }}
                          >
                            <FiX aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {!isTenantUser && (
              <small className={styles.hint}>
                {_t(
                  "skorTicketLinksHint",
                  "Link the profiles, applications or properties this is about (up to 10). Documents come from the first linked profile."
                )}
              </small>
            )}
          </div>
          <label className={styles.field} htmlFor="skor-ticket-subject">
            <span>{_t("skorTicketSubject", "Subject")}</span>
            <input
              id="skor-ticket-subject"
              autoFocus
              required
              value={subject}
              placeholder={_t(
                `${categorySettings.translationKey}SubjectPlaceholder`,
                categorySettings.subjectPlaceholder
              )}
              onChange={(event) => setSubject(event.target.value)}
            />
          </label>
          <div className={styles.twoColumns}>
            <label className={styles.field} htmlFor="skor-ticket-category">
              <span>{_t("skorTicketCategory", "Category")}</span>
              <select
                id="skor-ticket-category"
                required
                value={category}
                onChange={(event) => {
                  setCategory(event.target.value);
                }}
              >
                <option value="" disabled>
                  {_t("skorTicketSelectCategory", "Select a category")}
                </option>
                {visibleCategories.map((entry) => (
                  <option key={entry.value} value={entry.value}>
                    {_t(entry.translationKey, entry.label)}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field} htmlFor="skor-ticket-urgency">
              <span>{_t("skorTicketUrgency", "Urgency")}</span>
              <select
                id="skor-ticket-urgency"
                value={urgency}
                onChange={(event) => setUrgency(event.target.value)}
              >
                <option value="high">{_t("skorTicketHigh", "High")}</option>
                <option value="medium">
                  {_t("skorTicketMedium", "Medium")}
                </option>
                <option value="low">{_t("skorTicketLow", "Low")}</option>
              </select>
            </label>
          </div>
          <div className={styles.field}>
            <label htmlFor="skor-ticket-documents">
              {requiresDocuments
                ? _t("skorTicketDocumentsRequired", "Documents (required)")
                : _t("skorTicketDocuments", "Documents (optional)")}
            </label>
            <Select
              className={styles.documentSelect}
              menuPlacement="bottom"
              inputId="skor-ticket-documents"
              isMulti
              isDisabled={isSubmitting || !primaryTenantId}
              isLoading={loadingDocuments}
              options={primaryTenantId ? documentOptions : []}
              value={selectedDocuments}
              onChange={(value) => setSelectedDocuments(value || [])}
              classNamePrefix="skor-ticket-select"
              placeholder={_t("skorTicketSelectDocuments", "Select documents")}
              noOptionsMessage={() =>
                _t("skorTicketNoDocuments", "No documents available")
              }
            />
            {documentLoadFailed && (
              <small className={styles.hint}>
                {_t(
                  "skorTicketDocumentsUnavailable",
                  "Documents could not be loaded. Close and reopen this form to try again."
                )}
              </small>
            )}
          </div>
          <label className={styles.field} htmlFor="skor-ticket-message">
            <span>{_t("skorTicketMessage", "Message")}</span>
            <textarea
              id="skor-ticket-message"
              required
              rows={4}
              value={message}
              placeholder={_t(
                `${categorySettings.translationKey}MessagePlaceholder`,
                categorySettings.messagePlaceholder
              )}
              onChange={(event) => setMessage(event.target.value)}
            />
          </label>
          <div
            className={`${styles.dropArea} ${isDragging ? styles.dragging : ""}`}
            onDragOver={(event) => {
              event.preventDefault();
              if (!isSubmitting) setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragging(false);
              addAttachments(event.dataTransfer.files);
            }}
          >
            <button
              type="button"
              className={styles.uploadButton}
              onClick={() => fileInput.current?.click()}
            >
              <FiPaperclip aria-hidden="true" />
              {_t(
                "skorTicketAddAttachments",
                "Attach screenshots or PDFs (optional), or drag them here."
              )}
            </button>
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,application/pdf"
              multiple
              hidden
              onChange={(event) => {
                addAttachments(event.target.files);
                event.target.value = "";
              }}
            />
          </div>
          {fileError && (
            <p className={styles.error} role="alert">
              {_t(
                "skorTicketInvalidFile",
                "Please select up to 5 non-empty PNG, JPG, or PDF files (max 10MB each)."
              )}
            </p>
          )}
          {attachments.length > 0 && (
            <ul className={styles.attachments}>
              {attachments.map((file, index) => (
                <li
                  key={`${file.name}-${file.size}-${file.lastModified}`}
                  className={styles.profileChip}
                >
                  <span>{file.name}</span>
                  <button
                    type="button"
                    className={styles.removeButton}
                    aria-label={_t(
                      "skorTicketRemoveAttachment",
                      "Remove {{name}}",
                      "translation",
                      { name: file.name }
                    )}
                    onClick={() =>
                      setAttachments((current) =>
                        current.filter((_, fileIndex) => fileIndex !== index)
                      )
                    }
                  >
                    <FiX aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </fieldset>
        {error && (
          <p className={styles.error} role="alert">
            {error === "category"
              ? _t("skorTicketCategoryRequired", "Please select a category.")
              : error === "documents"
                ? _t(
                    "skorTicketDocumentsRequiredError",
                    "Select at least one document for this category."
                  )
                : error === "required"
                  ? _t(
                      "skorTicketRequired",
                      "Please enter a subject and message."
                    )
                  : _t(
                      "skorTicketSubmitError",
                      "Your ticket could not be submitted. Please try again."
                    )}
          </p>
        )}
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelButton}
            disabled={isSubmitting}
            onClick={closeModal}
          >
            {_t("skorTicketCancel", "Cancel")}
          </button>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={!canSubmit}
            aria-busy={isSubmitting}
          >
            {isSubmitting
              ? _t("skorTicketSubmitting", "Submitting…")
              : _t("skorTicketSubmit", "Submit ticket")}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
